// Fontes da composição — as MESMAS do edvid (Poppins, Playfair Display, Lora, Libre Baskerville,
// Inter), com os mesmos pesos/estilos. Os metadados (arquivos woff2, unicode-range, nome da
// família) vêm do @remotion/google-fonts via getInfo(), então os glifos são idênticos aos do
// loadFont() do edvid. A diferença: o arquivo é buscado primeiro em `${assetBase}/fonts/<arquivo>`
// (cópia local, ver render-worker/scripts/fetch-fonts.mjs) e só cai no fonts.gstatic.com se a cópia
// local falhar — o render-worker roda sem internet pública.
//
// measureText() do @remotion/layout-utils tem CACHE por texto+fonte: medir antes da fonte carregar
// grava a largura da fonte substituta para sempre. Por isso as camadas de texto só montam depois
// de useFontsReady() (ver Main.tsx) — no render o delayRender segura o frame até lá.
import { useEffect, useState } from 'react';
import { continueRender, delayRender } from 'remotion';
import { getInfo as poppinsInfo } from '@remotion/google-fonts/Poppins';
import { getInfo as playfairInfo } from '@remotion/google-fonts/PlayfairDisplay';
import { getInfo as loraInfo } from '@remotion/google-fonts/Lora';
import { getInfo as baskervilleInfo } from '@remotion/google-fonts/LibreBaskerville';
import { getInfo as interInfo } from '@remotion/google-fonts/Inter';

type Info = {
  fontFamily: string;
  fonts: Record<string, Record<string, Record<string, string>>>;
  unicodeRanges: Record<string, string>;
};

const INFO = {
  poppins: poppinsInfo() as unknown as Info,
  playfair: playfairInfo() as unknown as Info,
  lora: loraInfo() as unknown as Info,
  baskerville: baskervilleInfo() as unknown as Info,
  inter: interInfo() as unknown as Info,
};

export const POPPINS = INFO.poppins.fontFamily;
export const PLAYFAIR = INFO.playfair.fontFamily;
export const LORA = INFO.lora.fontFamily;
export const BASKERVILLE = INFO.baskerville.fontFamily;
export const INTER = INFO.inter.fontFamily;

// Pesos/estilos exatamente como os loadFont() do edvid (Main, Stacked, Scatter, Simple).
export const FONT_REQUESTS: Array<{ key: keyof typeof INFO; style: 'normal' | 'italic'; weights: string[] }> = [
  { key: 'poppins', style: 'normal', weights: ['400', '600', '700', '800', '900'] },
  { key: 'poppins', style: 'italic', weights: ['700', '900'] },
  { key: 'playfair', style: 'italic', weights: ['700', '900'] },
  { key: 'playfair', style: 'normal', weights: ['700', '900'] },
  { key: 'lora', style: 'normal', weights: ['400', '600'] },
  { key: 'lora', style: 'italic', weights: ['400', '600'] },
  { key: 'baskerville', style: 'normal', weights: ['700'] },
  { key: 'inter', style: 'normal', weights: ['500'] },
];

/** Português usa latin (+ latin-ext para aspas/traços raros). */
export const FONT_SUBSETS = ['latin', 'latin-ext'];

export interface FontFile {
  family: string;
  style: string;
  weight: string;
  unicodeRange: string;
  remoteUrl: string;
  fileName: string;
}

const isChunk = (s: string) => /^\[\d+\]$/.test(s);

/** Lista de arquivos woff2 necessários (usada também pelo script que baixa as cópias locais). */
export function listFontFiles(): FontFile[] {
  const out: FontFile[] = [];
  const seen = new Set<string>();
  for (const req of FONT_REQUESTS) {
    const info = INFO[req.key];
    for (const weight of req.weights) {
      const bySubset = info.fonts[req.style]?.[weight];
      if (!bySubset) continue;
      const available = Object.keys(bySubset);
      const subsets = new Set<string>();
      for (const s of FONT_SUBSETS) {
        if (available.includes(s)) subsets.add(s);
        else available.filter(isChunk).forEach((c) => subsets.add(c));
      }
      for (const subset of subsets) {
        const remoteUrl = bySubset[subset];
        const key = `${info.fontFamily}-${req.style}-${weight}-${subset}`;
        if (!remoteUrl || seen.has(key)) continue;
        seen.add(key);
        out.push({
          family: info.fontFamily,
          style: req.style,
          weight,
          unicodeRange: info.unicodeRanges[subset],
          remoteUrl,
          fileName: remoteUrl.split('/').pop() || `${key}.woff2`,
        });
      }
    }
  }
  return out;
}

const loaded = new Map<string, Promise<void>>();

async function loadOne(f: FontFile, assetBase: string): Promise<void> {
  const sources = [`${assetBase.replace(/\/$/, '')}/fonts/${f.fileName}`, f.remoteUrl];
  let lastErr: unknown = null;
  for (const src of sources) {
    try {
      const face = new FontFace(f.family, `url(${src}) format('woff2')`, {
        weight: f.weight,
        style: f.style,
        unicodeRange: f.unicodeRange,
      });
      await face.load();
      document.fonts.add(face);
      return;
    } catch (err) {
      lastErr = err;
    }
  }
  // eslint-disable-next-line no-console
  console.warn(`[video-compositions] fonte não carregou: ${f.family} ${f.style} ${f.weight}`, lastErr);
}

export function loadAllFonts(assetBase: string): Promise<void> {
  if (typeof FontFace === 'undefined' || typeof document === 'undefined') return Promise.resolve();
  const key = assetBase;
  let p = loaded.get(key);
  if (!p) {
    p = Promise.all(listFontFiles().map((f) => loadOne(f, assetBase))).then(() => undefined);
    loaded.set(key, p);
  }
  return p;
}

let ready = false;
const continued = new Set<number>();

/** true quando todas as fontes estão prontas. No render, segura o frame com delayRender. */
export function useFontsReady(assetBase: string): boolean {
  const [ok, setOk] = useState(ready);
  const [handle] = useState(() => (ready ? null : delayRender('video-compositions: fontes', { timeoutInMilliseconds: 60000 })));
  useEffect(() => {
    let alive = true;
    loadAllFonts(assetBase).then(() => {
      ready = true;
      if (alive) setOk(true);
      if (handle !== null && !continued.has(handle)) {
        continued.add(handle);
        continueRender(handle);
      }
    });
    return () => {
      alive = false;
    };
  }, [assetBase, handle]);
  return ok;
}
